"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { FIELD_UNDERLINE, FieldShell } from "@/components/graphite/field"
import { PageAction } from "@/components/graphite/page-header"
import { QuietAction } from "@/components/graphite/surface"
import { cn } from "@/lib/utils"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
//import { Card, CardContent } from "@/components/ui/card"
import { Pencil } from "lucide-react"
//import { CompanyAutocomplete } from "@/components/community/company-autocomplete"
import { Job } from "openapi/api/model"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  getGetJobsJobIdQueryKey,
  getGetJobsQueryKey,
  useGetJobsJobId,
  usePutJobsJobId,
} from "openapi/api/endpoints"
//import type { Job } from "@/contexts/job-assignments-context"

/* interface HiringManager {
  id: string
  name: string
  email: string
  position: string
} */

interface JobEditData {
  title: string
  department: string
  location: string
  type: "full_time" | "part_time" | "contract" | "intern"
  salary_range: string
  description: string
  //company_name: string
  //hiringManagers: HiringManager[]
  status: "active" | "paused" | "closed" | "draft"
}

interface JobEditDialogProps {
  jobId: string
  trigger?: React.ReactNode
  onJobUpdated?: (jobId: string, jobData: Partial<Job>) => void
  /**
   * Controlled mode, for a caller that opens the dialog from somewhere the
   * dialog cannot live — a dropdown item, whose content unmounts when the menu
   * closes. When `open` is given, no trigger is drawn.
   */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function JobEditDialog({
  jobId,
  trigger,
  onJobUpdated,
  open,
  onOpenChange,
}: JobEditDialogProps) {
  const [ownOpen, setOwnOpen] = useState(false)
  const isControlled = open !== undefined
  const isOpen = isControlled ? open : ownOpen
  const setIsOpen = (next: boolean) => (isControlled ? onOpenChange?.(next) : setOwnOpen(next))
  const { data:job } = useGetJobsJobId(jobId)
  const queryClient = useQueryClient()
  const { mutateAsync: saveJob, isPending: isSaving } = usePutJobsJobId()

  // Parse hiring managers from string array to structured objects
  /* const parseHiringManagers = (managers: string[]): HiringManager[] => {
    return managers.map((manager, index) => {
      // Try to parse "Name (email)" format
      const match = manager.match(/^(.+?)\s*$$(.+?)$$$/)
      if (match) {
        return {
          id: `${index}`,
          name: match[1].trim(),
          email: match[2].trim(),
          position: "",
        }
      }
      // Fallback to just name
      return {
        id: `${index}`,
        name: manager,
        email: "",
        position: "",
      }
    })
  }
 */
  const [jobData, setJobData] = useState<JobEditData>({
    title: job?.title??'',
    department: job?.department??'',
    location: job?.location??'',
    type: job?.type as "full_time" | "part_time" | "contract" | "intern",
    salary_range: job?.salary_range??'',
    description: job?.description??'',
    //company_name: job?.company_name??'',
    //hiringManagers: parseHiringManagers(job.hiringManagers),
    status: job?.status as "active" | "paused" | "closed" | "draft",
  })

  /* const [newHiringManager, setNewHiringManager] = useState({
    name: "",
    email: "",
    position: "",
  }) */

  // Reset form when dialog opens with fresh job data
  useEffect(() => {
    if (isOpen) {
      setJobData({
        title: job?.title??'',
        department: job?.department??'',
        location: job?.location??'',
        type: job?.type as "full_time" | "part_time" | "contract" | "intern",
        salary_range: job?.salary_range??'',
        description: job?.description??'',
        //company_name: job?.company_name??'',
        //hiringManagers: parseHiringManagers(job.hiringManagers),
        status: job?.status as "active" | "paused" | "closed" | "draft",
      })
    }
  }, [isOpen, job])

  /* const handleAddHiringManager = () => {
    if (newHiringManager.name && newHiringManager.email) {
      setJobData((prev) => ({
        ...prev,
        hiringManagers: [
          ...prev.hiringManagers,
          {
            id: Date.now().toString(),
            ...newHiringManager,
          },
        ],
      }))
      setNewHiringManager({ name: "", email: "", position: "" })
    }
  }

  const handleRemoveHiringManager = (id: string) => {
    setJobData((prev) => ({
      ...prev,
      hiringManagers: prev.hiringManagers.filter((hm) => hm.id !== id),
    }))
  } */

  /*
    The dialog saves the job itself. It used to only hand the edits to an
    optional `onJobUpdated`, and no caller ever passed one — so Edit job on
    every job, desktop and mobile, closed on Save and threw the edits away.
    A failed save keeps the dialog and the form, so nothing typed is lost.
  */
  const handleSubmit = async () => {
    // Format hiring managers back to string array
    //const formattedManagers = jobData.hiringManagers.map((hm) => `${hm.name} (${hm.email})`)

    const updatedData = {
      title: jobData.title,
      department: jobData.department,
      location: jobData.location,
      type: jobData.type,
      salary_range: jobData.salary_range,
      description: jobData.description,
      //company_name: jobData.company_name,
      //hiringManagers: formattedManagers,
      status: jobData.status,
    }

    try {
      await saveJob({ jobId, data: updatedData })
    } catch (error) {
      toast.error("The job could not be saved", { description: `${error}` })
      return
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getGetJobsJobIdQueryKey(jobId) }),
      queryClient.invalidateQueries({ queryKey: getGetJobsQueryKey() }),
    ])
    onJobUpdated?.(jobId, updatedData)
    setIsOpen(false)
  }

  const canSubmit = jobData.title && jobData.department && jobData.location //&& jobData.company_name

  const CONTROL = cn(FIELD_UNDERLINE, "pb-[7px] text-[12.5px] leading-[1.4]")
  const TRIGGER = cn(CONTROL, "h-auto")
  const required = (label: string) => (
    <>
      {label} <span className="text-destructive">*</span>
    </>
  )

  return (
    <>
      {isControlled ? null : trigger ? (
        <span onClick={() => setIsOpen(true)}>{trigger}</span>
      ) : (
        <QuietAction onClick={() => setIsOpen(true)}>
          <Pencil className="size-3.5" />
          Edit Job
        </QuietAction>
      )}

      <DialogShell
        open={isOpen}
        onOpenChange={setIsOpen}
        wide
        title="Edit Job"
        footer={
          <>
            <QuietAction size="md" onClick={() => setIsOpen(false)}>
              Cancel
            </QuietAction>
            <PageAction tone="primary" onClick={() => void handleSubmit()} disabled={!canSubmit || isSaving}>
              {isSaving ? "Saving…" : "Save Changes"}
            </PageAction>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-5 @sm:grid-cols-2">
          <FieldShell label={required("Job title")}>
            <input
              id="jobTitle"
              value={jobData.title}
              onChange={(e) => setJobData((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Senior Python Developer"
              aria-label="Job title"
              className={CONTROL}
            />
          </FieldShell>
          <FieldShell label="Job type">
            <Select
              value={jobData.type}
              onValueChange={(value: any) => setJobData((prev) => ({ ...prev, type: value }))}
            >
              <SelectTrigger aria-label="Job type" className={TRIGGER}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full_time">Full-time</SelectItem>
                <SelectItem value="part_time">Part-time</SelectItem>
                <SelectItem value="contract">Contract</SelectItem>
                <SelectItem value="intern">Internship</SelectItem>
              </SelectContent>
            </Select>
          </FieldShell>
        </div>

        <FieldShell label="Status" className="mt-5">
          <Select
            value={jobData.status}
            onValueChange={(value: any) => setJobData((prev) => ({ ...prev, status: value }))}
          >
            <SelectTrigger aria-label="Status" className={TRIGGER}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
            </SelectContent>
          </Select>
        </FieldShell>

        <div className="mt-6 grid grid-cols-1 gap-5 border-t pt-5 @sm:grid-cols-2">
          <FieldShell label={required("Department")}>
            <input
              id="department"
              value={jobData.department}
              onChange={(e) => setJobData((prev) => ({ ...prev, department: e.target.value }))}
              placeholder="e.g., Engineering"
              aria-label="Department"
              className={CONTROL}
            />
          </FieldShell>
          <FieldShell label={required("Location")}>
            <input
              id="location"
              value={jobData.location}
              onChange={(e) => setJobData((prev) => ({ ...prev, location: e.target.value }))}
              placeholder="e.g., San Francisco, CA"
              aria-label="Location"
              className={CONTROL}
            />
          </FieldShell>
        </div>

        <FieldShell label="Salary range" className="mt-5">
          <input
            id="salary_range"
            value={jobData.salary_range}
            onChange={(e) => setJobData((prev) => ({ ...prev, salary_range: e.target.value }))}
            placeholder="e.g., $120,000 - $180,000"
            aria-label="Salary range"
            className={CONTROL}
          />
        </FieldShell>

        <FieldShell label="Job description" className="mt-5">
          <textarea
            id="description"
            value={jobData.description}
            onChange={(e) => setJobData((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Detailed job description..."
            rows={5}
            aria-label="Job description"
            className={cn(FIELD_UNDERLINE, "resize-none pb-2.5 text-[12.5px] leading-[1.7]")}
          />
        </FieldShell>
      </DialogShell>
    </>
  )
}
